import React, { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import {
  getInitiativeImpact,
  initiativeErrorMessage,
} from "../services/efficiencyInitiativesApi";
import InitiativeInsightLoss from "./InitiativeInsightLoss";

/**
 * Measured impact of one initiative — the energy and money view inside the detail
 * modal, with the insight-loss view rendered beneath it.
 *
 * Every number here comes from the server's before/after comparison of meter
 * data; nothing is computed in the browser. Two presentation rules follow from
 * that, and both matter:
 *
 *   * A regression is shown as a regression. Consumption going UP after an
 *     initiative is a real and useful finding, so negative savings render in red
 *     rather than being clamped to zero or hidden.
 *   * When the server says the windows lack coverage, no numbers are drawn at
 *     all. A savings figure derived from four hours of data is worse than no
 *     figure, because someone will act on it.
 *
 * The two halves are rendered independently. Energy and insight loss are computed
 * from different tables over different meter sets and can fail separately, so an
 * unmeasurable energy comparison must not blank the insight-loss attribution or
 * the reverse — the insight-loss section is therefore OUTSIDE every early return
 * below.
 */

const GRAINS = ["daily", "weekly", "monthly", "yearly"];

const PKR = new Intl.NumberFormat("en-PK", { maximumFractionDigits: 0 });
const NUM = new Intl.NumberFormat("en-PK", { maximumFractionDigits: 1 });

const fmtPkr = (v) => (v == null ? "—" : `PKR ${PKR.format(v)}`);
const fmtKwh = (v) => (v == null ? "—" : `${NUM.format(v)} kWh`);
const fmtKw = (v) => (v == null ? "—" : `${NUM.format(v)} kW`);

/** Tonnes once it stops being legible in kg. */
const fmtCo2 = (kg) => {
  if (kg == null) return "—";
  return Math.abs(kg) >= 1000
    ? `${NUM.format(kg / 1000)} t CO₂`
    : `${NUM.format(kg)} kg CO₂`;
};

const fmtPayback = (months) => {
  if (months == null) return "No payback";
  return months < 24
    ? `${NUM.format(months)} months`
    : `${NUM.format(months / 12)} years`;
};

export default function InitiativeImpactPanel({ initiative, username }) {
  const [grain, setGrain] = useState("weekly");
  const [impact, setImpact] = useState(null);
  const [insightLoss, setInsightLoss] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!initiative?.initiative_id || !username) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getInitiativeImpact(initiative.initiative_id, username, grain)
      .then((data) => {
        if (cancelled) return;
        setImpact(data.impact);
        setInsightLoss(data.insightLoss);
      })
      .catch((err) => {
        if (!cancelled) setError(initiativeErrorMessage(err, "Could not compute impact."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [initiative?.initiative_id, username, grain]);

  // Baseline and post-implementation periods are stitched into ONE chronological
  // series with a marker at the split, rather than drawn as two overlaid series.
  // Two series on a shared axis would imply the periods are contemporaneous; they
  // are consecutive, and the whole point is to see the level change at the split.
  const chart = useMemo(() => {
    if (!impact?.trends) return null;
    const before = (impact.trends.baseline || []).map((p) => ({
      x: new Date(p.period).getTime(),
      y: p.kwh,
    }));
    const after = (impact.trends.after || []).map((p) => ({
      x: new Date(p.period).getTime(),
      y: p.kwh,
    }));
    if (before.length === 0 && after.length === 0) return null;

    const splitAt = impact.after?.start ? new Date(impact.after.start).getTime() : null;
    const isGen = impact.metric === "generation";

    const annotations = { xaxis: [], yaxis: [] };
    if (splitAt) {
      annotations.xaxis.push({
        x: splitAt,
        borderColor: "#1ba08e",
        strokeDashArray: 4,
        label: {
          text: "implemented",
          style: { background: "#1ba08e", color: "#fff", fontSize: "10px" },
        },
      });
    }
    // Horizontal average lines make the level shift readable even when the daily
    // series is noisy — which, on real meter data, it always is.
    if (impact.energy?.avg_daily_before_kwh != null && grain === "daily") {
      annotations.yaxis.push({
        y: impact.energy.avg_daily_before_kwh,
        borderColor: "#94a3b8",
        strokeDashArray: 3,
        label: { text: "before avg", style: { background: "#94a3b8", fontSize: "10px" } },
      });
      annotations.yaxis.push({
        y: impact.energy.avg_daily_after_kwh,
        borderColor: isGen ? "#22c55e" : "#0ea5e9",
        strokeDashArray: 3,
        label: {
          text: "after avg",
          style: { background: isGen ? "#22c55e" : "#0ea5e9", fontSize: "10px" },
        },
      });
    }

    return {
      series: [
        { name: "Baseline", data: before },
        { name: "After implementation", data: after },
      ],
      options: {
        chart: { type: "area", height: 260, toolbar: { show: false }, animations: { enabled: false } },
        colors: ["#94a3b8", "#1ba08e"],
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 2 },
        fill: { type: "gradient", gradient: { opacityFrom: 0.35, opacityTo: 0.05 } },
        xaxis: { type: "datetime", labels: { style: { fontSize: "11px" } } },
        yaxis: {
          title: { text: `kWh per ${grain === "daily" ? "day" : grain.replace("ly", "")}` },
          labels: { formatter: (v) => NUM.format(v) },
        },
        legend: { position: "top", horizontalAlign: "right", fontSize: "12px" },
        grid: { borderColor: "#e2e8f0", strokeDashArray: 3 },
        annotations,
        tooltip: { x: { format: "dd MMM yyyy" }, y: { formatter: (v) => `${NUM.format(v)} kWh` } },
      },
    };
  }, [impact, grain]);

  if (loading) {
    // Named, not a bare spinner: the insight-loss half runs the enSight detectors
    // over both windows and takes several seconds on a real node, so an unlabelled
    // wait reads as a hang. The skeleton tiles show how many figures are coming,
    // which stops the section from appearing to grow as they land.
    return (
      <div className="ei-impact">
        <div className="ei-impact-head">
          <h4>Measured impact</h4>
        </div>
        <p className="ei-loading-row" role="status">
          <span className="ei-spinner" aria-hidden="true" />
          Computing impact and comparing insights — a few seconds.
        </p>
        <div className="ei-kpis" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => (
            <div className="ei-kpi" key={i}>
              <span className="ei-skeleton w-60" />
              <span className="ei-skeleton w-80" />
              <span className="ei-skeleton w-40" />
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (error) {
    return <div className="ei-alert error">{error}</div>;
  }
  if (!impact) return null;

  // Grains whose windows are too short collapse to a single point, which draws
  // as an isolated dot: offering them makes the control look broken. The server
  // reports which are usable.
  const grainInfo = impact?.trends?.available_grains || [];
  const grainMeta = (g) => grainInfo.find((x) => x.grain === g);

  const grainPicker = (
    <div className="ei-impact-grain">
      {GRAINS.map((g) => {
        const meta = grainMeta(g);
        const disabled = meta ? !meta.usable : false;
        return (
          <button
            key={g}
            className={`ei-grain-btn${g === grain ? " active" : ""}`}
            onClick={() => setGrain(g)}
            disabled={disabled}
            title={
              disabled
                ? meta.reason
                : meta
                ? `${meta.baseline_points} baseline / ${meta.after_points} after points`
                : undefined
            }
          >
            {g[0].toUpperCase() + g.slice(1)}
          </button>
        );
      })}
    </div>
  );

  if (!impact.measurable) {
    return (
      <div className="ei-impact">
        <div className="ei-impact-head">
          <h4>Measured impact</h4>
          {grainPicker}
        </div>
        <div className="ei-alert info">
          <strong>Not enough data to measure the energy change yet.</strong>
          <div style={{ marginTop: "0.35rem", fontWeight: 400 }}>{impact.reason}</div>
          {impact.warning && (
            <div style={{ marginTop: "0.35rem", fontWeight: 400 }}>⚠ {impact.warning}</div>
          )}
        </div>
        <ConfidenceBadge confidence={impact.confidence} />
        <MeasurementBasis impact={impact} />
        {/* Still rendered: insight loss reads minute-level data over the node's
            whole subtree, so it can be measurable when the hourly energy
            comparison is not. */}
        <InitiativeInsightLoss insightLoss={insightLoss} />
      </div>
    );
  }

  const e = impact.energy;
  const f = impact.financial;
  const p = impact.peak_demand;
  const co2 = impact.co2;
  const ev = impact.expected_vs_actual;
  const isGen = impact.metric === "generation";
  const improved = e.avg_daily_saving_kwh > 0;

  return (
    <div className="ei-impact">
      <div className="ei-impact-head">
        <h4>
          Measured impact{" "}
          <span className="ei-impact-sub">
            {isGen ? "generation" : "consumption"} · measured over {e.measured_over_days} days
          </span>
        </h4>
      </div>

      {!improved && (
        <div className="ei-alert error" style={{ marginBottom: "1rem" }}>
          <strong>Underperforming.</strong> {isGen ? "Generation" : "Consumption"} moved the
          wrong way after implementation — {isGen ? "down" : "up"}{" "}
          {fmtKwh(Math.abs(e.avg_daily_saving_kwh))}/day
          {e.percent_change != null ? ` (${NUM.format(Math.abs(e.percent_change))}%)` : ""}.
        </div>
      )}

      {/* Rendered here as well as on the not-measurable path. The window warnings
          fire precisely when the comparison DID produce numbers — an overlapping
          baseline still yields a figure, it just yields a meaningless one — so
          showing them only when there is nothing to show is backwards. */}
      {impact.warning && (
        <div className="ei-alert warn" style={{ marginBottom: "1rem" }}>
          ⚠ {impact.warning}.
        </div>
      )}

      <ConfidenceBadge confidence={impact.confidence} />

      <div className="ei-kpis">
        <Kpi label="Energy saved" value={fmtKwh(e.realised_saving_kwh)}
             sub={`${fmtKwh(e.annualised_saving_kwh)}/yr`} good={improved} />
        <Kpi label="Financial saving" value={fmtPkr(f.realised_saving_pkr)}
             sub={`${fmtPkr(f.annualised_saving_pkr)}/yr`} good={f.realised_saving_pkr > 0} />
        <Kpi label="Payback" value={fmtPayback(f.simple_payback_months)}
             sub={`cost ${fmtPkr(f.implementation_cost_pkr)}`}
             good={f.simple_payback_months != null} />
        {/* CO2 is reported as N/A until a verified grid emission factor is
            agreed. An invented factor produces a number that reads as
            authoritative and gets quoted onward, so `available: false` renders
            as N/A rather than as zero. */}
        <Kpi
          label="CO₂ avoided"
          value={co2.available ? fmtCo2(co2.avoided_kg) : "N/A"}
          sub={co2.available ? `@ ${co2.factor_kg_per_kwh} kg/kWh` : "no emission factor set"}
          good={co2.available ? co2.avoided_kg > 0 : true}
        />
        <Kpi label="Peak demand" value={fmtKw(p.reduction_kw)}
             sub={`${fmtKw(p.before_kw)} → ${fmtKw(p.after_kw)}`}
             good={p.reduction_kw != null && p.reduction_kw > 0} />
        <Kpi label="Net position" value={fmtPkr(f.net_position_pkr)}
             sub={f.net_position_pkr >= 0 ? "cost recovered" : "still recovering cost"}
             good={f.net_position_pkr >= 0} />
      </div>

      {impact.tariff?.peak_cheaper_than_offpeak && (
        <div className="ei-alert error">
          <strong>Tariff looks wrong.</strong> This client's peak rate (PKR{" "}
          {impact.tariff.peak_price_pkr_per_kwh}/kWh) is <em>below</em> the off-peak rate (PKR{" "}
          {impact.tariff.offpeak_price_pkr_per_kwh}/kWh). Financial savings — especially for
          load-shifting initiatives — will be misleading until the profile is corrected.
        </div>
      )}

      {chart && (
        <>
          <div className="ei-chart-head">
            <p className="ei-detail-label" style={{ margin: 0 }}>
              Before and after consumption trend
            </p>
            {grainPicker}
          </div>
          <p className="ei-rank-note">
            Grain changes the chart buckets only — the figures above are per-day
            averages and are the same at every grain.
          </p>
          <div className="ei-impact-chart">
            <ReactApexChart
              options={chart.options}
              series={chart.series}
              type="area"
              height={260}
            />
          </div>
        </>
      )}

      <div className="ei-impact-cols">
        <div>
          <p className="ei-detail-label">Before vs after</p>
          <table className="ei-mini-table">
            <tbody>
              <tr>
                <td>Avg daily {isGen ? "generation" : "consumption"}</td>
                <td className="ei-num">{fmtKwh(e.avg_daily_before_kwh)}</td>
                <td className="ei-num">{fmtKwh(e.avg_daily_after_kwh)}</td>
              </tr>
              <tr>
                <td>Avg daily cost</td>
                <td className="ei-num">{fmtPkr(f.avg_daily_cost_before_pkr)}</td>
                <td className="ei-num">{fmtPkr(f.avg_daily_cost_after_pkr)}</td>
              </tr>
              <tr>
                <td>Peak demand</td>
                <td className="ei-num">{fmtKw(p.before_kw)}</td>
                <td className="ei-num">{fmtKw(p.after_kw)}</td>
              </tr>
              <tr>
                <td>Change</td>
                <td className="ei-num" colSpan={2}>
                  <strong className={improved ? "ei-good" : "ei-bad"}>
                    {e.percent_change != null
                      ? `${e.percent_change > 0 ? "−" : "+"}${NUM.format(Math.abs(e.percent_change))}%`
                      : "n/a"}
                  </strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div>
          <p className="ei-detail-label">Expected vs actual (annualised)</p>
          <table className="ei-mini-table">
            <tbody>
              <tr>
                <td>Energy</td>
                <td className="ei-num">{fmtKwh(ev.expected_annual_kwh)}</td>
                <td className="ei-num">{fmtKwh(ev.actual_annualised_kwh)}</td>
              </tr>
              <tr>
                <td>Money</td>
                <td className="ei-num">{fmtPkr(ev.expected_annual_pkr)}</td>
                <td className="ei-num">{fmtPkr(ev.actual_annualised_pkr)}</td>
              </tr>
              <tr>
                <td>Attainment</td>
                <td className="ei-num" colSpan={2}>
                  <strong
                    className={
                      ev.kwh_attainment_percent == null
                        ? ""
                        : ev.kwh_attainment_percent >= 100
                        ? "ei-good"
                        : "ei-bad"
                    }
                  >
                    {ev.kwh_attainment_percent == null
                      ? "no expectation set"
                      : `${NUM.format(ev.kwh_attainment_percent)}% of expected`}
                  </strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <NormalisationResult normalisation={impact.normalisation} />

      <MeasurementBasis impact={impact} />

      <InitiativeInsightLoss insightLoss={insightLoss} />
    </div>
  );
}

/**
 * The normalised saving, next to the raw one.
 *
 * Never instead of it. The raw figure is what the meter says; the normalised
 * figure is what the meter says once the change in output, running hours, weather
 * or headcount is accounted for. The gap between them is the finding — "you saved
 * 3% on the meter but 19% per tonne" tells an operator something neither number
 * tells alone — so both are always on screen with the driver that separates them.
 *
 * Renders nothing when the initiative is not normalised, rather than an empty
 * card: most initiatives are not, and a permanent "N/A" panel trains people to
 * ignore the area where the real answer will eventually appear.
 */
function NormalisationResult({ normalisation }) {
  const n = normalisation;
  if (!n || n.variable === "none") return null;

  const label = (n.variable || "").replace(/_/g, " ");

  if (!n.applied) {
    return (
      <div className="ei-norm-result">
        <p className="ei-detail-label">Normalised against {label}</p>
        <div className="ei-alert info">
          <strong>Not normalised yet.</strong>
          <div style={{ marginTop: "0.3rem", fontWeight: 400 }}>{n.reason}</div>
          {/* The driver summaries survive a refusal, so show what IS there —
              "you have 12 of 30 days" is actionable, "unavailable" is not. */}
          {n.baseline_driver && n.after_driver && (
            <div style={{ marginTop: "0.4rem", fontWeight: 400 }}>
              Readings cover {n.baseline_driver.coverage_percent}% of the baseline and{" "}
              {n.after_driver.coverage_percent}% of the period since implementation.
            </div>
          )}
        </div>
      </div>
    );
  }

  const rv = n.raw_vs_normalised;
  const better = n.normalised_saving_kwh_per_day > rv.raw_saving_kwh_per_day;

  return (
    <div className="ei-norm-result">
      <p className="ei-detail-label">Adjusted for {label}</p>

      {/* Two numbers, side by side. The gap between them IS the finding, so the
          raw figure is never replaced — a portfolio total that silently mixed
          normalised and unnormalised initiatives would be indefensible. */}
      <div className="ei-norm-compare">
        <div>
          <span className="ei-norm-label">On the meter</span>
          <span className="ei-norm-value">
            {NUM.format(rv.raw_saving_kwh_per_day)} kWh/day
          </span>
        </div>
        <span className="ei-norm-arrow">→</span>
        <div>
          <span className="ei-norm-label">Adjusted for {label}</span>
          <span className={`ei-norm-value ${better ? "ei-good" : "ei-bad"}`}>
            {NUM.format(rv.normalised_saving_kwh_per_day)} kWh/day
          </span>
        </div>
        <div className="ei-norm-annual">
          {fmtKwh(n.normalised_annualised_saving_kwh)}/yr
          {n.financial && ` · ${fmtPkr(n.financial.annualised_saving_pkr)}/yr`}
        </div>
      </div>

      {/* The one sentence that explains why the two differ. */}
      {(n.warnings || []).length > 0 && (
        <p className="ei-rank-note">{n.warnings[0]}</p>
      )}

      <details className="ei-norm-details">
        <summary>How this was adjusted</summary>
        <div className="ei-basis-body">
          <p>
            <strong>{label} rate:</strong> {NUM.format(rv.driver_per_day_before)} →{" "}
            {NUM.format(rv.driver_per_day_after)} per day
            {n.baseline_driver?.unit ? ` (${n.baseline_driver.unit})` : ""}.{" "}
            <strong>Energy intensity:</strong> {NUM.format(n.baseline_intensity)} →{" "}
            {NUM.format(n.after_intensity)} {n.intensity_unit}.
          </p>
          <p>
            At the old efficiency, current output would have needed{" "}
            <strong>{NUM.format(n.adjusted_baseline_avg_daily_kwh)} kWh/day</strong>.
            The saving is that minus what was actually used.
          </p>
          <p>{n.method}</p>
          {n.warnings && n.warnings.length > 1 && (
            <p>
              <strong>Also:</strong> {n.warnings.slice(1).join("; ")}.
            </p>
          )}
        </div>
      </details>
    </div>
  );
}

/**
 * Confidence in the measured impact: one word, plus the reason it is not higher.
 *
 * The level is deliberately not a number. A score of 84 invites "why not 85?",
 * whereas High/Medium/Low prompts the useful question — what is holding it back —
 * which the limiting factor answers on the same line. The score is still in the
 * expanded view for anyone who wants it.
 *
 * "Unavailable" is rendered differently from "Low" because they mean different
 * things: Low is a measured figure to treat as indicative, Unavailable means there
 * is nothing to be confident about.
 */
function ConfidenceBadge({ confidence }) {
  if (!confidence?.level) return null;

  const { level, score, summary, limiting_factor: limiting, checks = [], notes = [] } =
    confidence;
  const label = { high: "High", medium: "Medium", low: "Low", unavailable: "N/A" }[level]
    || level;

  return (
    <div className={`ei-confidence ${level}`}>
      <div className="ei-confidence-head">
        <span className="ei-confidence-dot" aria-hidden="true" />
        <span className="ei-confidence-level">{label} confidence</span>
        <span className="ei-confidence-summary">{summary}</span>
      </div>

      {checks.length > 0 && (
        <details className="ei-norm-details">
          <summary>How this was judged</summary>
          <p
            className="ei-hint"
            style={{ marginTop: "0.5rem" }}
            title={`Scored on ${confidence.basis}. Capped by the weakest check, not averaged.`}
          >
            {score}/100, capped by the weakest check.
          </p>
          <ul className="ei-confidence-checks">
            {checks.map((c) => (
              <li key={c.name} className={c.verdict}>
                <span className="ei-confidence-check-verdict">{c.verdict}</span>
                <span>
                  <strong>{c.label}</strong> — {c.detail}
                </span>
              </li>
            ))}
          </ul>
          {notes.map((n, i) => (
            <p key={i} className="ei-hint">⚠ {n}</p>
          ))}
        </details>
      )}

      {checks.length === 0 && limiting && (
        <p className="ei-hint" style={{ margin: "0.35rem 0 0" }}>{limiting}</p>
      )}
    </div>
  );
}


function Kpi({ label, value, sub, good }) {
  return (
    <div className={`ei-kpi${good ? "" : " negative"}`}>
      <p className="ei-kpi-label">{label}</p>
      <p className="ei-kpi-value">{value}</p>
      <p className="ei-kpi-sub">{sub}</p>
    </div>
  );
}

/**
 * How the number was arrived at. Collapsed by default but always present: an
 * aggregated Building figure that silently drops four of its meters is
 * indefensible unless the caller can see which meters were used and why.
 */
function MeasurementBasis({ impact }) {
  const [open, setOpen] = useState(false);
  const n = impact.node || {};
  return (
    <div className="ei-basis">
      <button className="ei-basis-toggle" onClick={() => setOpen((v) => !v)}>
        {open ? "▾" : "▸"} How this was measured
      </button>
      {open && (
        <div className="ei-basis-body">
          <p>
            <strong>Node:</strong> {n.node_name} ({n.node_type}
            {n.category ? `, ${n.category}` : ""}) · resolution:{" "}
            <code>{n.resolution_mode}</code>
          </p>
          <p>
            <strong>Meters summed:</strong>{" "}
            {(n.meters_used || []).length
              ? n.meters_used.map((m) => `${m.machine_name} (${m.category})`).join(", ")
              : "none"}
          </p>
          {(n.meters_excluded || []).length > 0 && (
            <p>
              <strong>Deliberately excluded:</strong>
              <ul>
                {n.meters_excluded.map((m) => (
                  <li key={m.meter_id}>
                    {m.machine_name} ({m.category}) — {m.reason}
                  </li>
                ))}
              </ul>
            </p>
          )}
          {impact.baseline && impact.after && (
            <p>
              <strong>Windows:</strong> baseline {impact.baseline.start} → {impact.baseline.end}{" "}
              ({impact.baseline.covered_days} usable days) · after {impact.after.start} →{" "}
              {impact.after.end} ({impact.after.covered_days} usable days). Only days with at
              least 12 hours of readings count.
            </p>
          )}
          {impact.tariff && (
            <p>
              <strong>Tariff:</strong> peak PKR {impact.tariff.peak_price_pkr_per_kwh}/kWh,
              off-peak PKR {impact.tariff.offpeak_price_pkr_per_kwh}/kWh. Cost is split by
              tariff window, so load shifting registers even when total kWh is unchanged.
            </p>
          )}
          {impact.peak_demand?.definition && (
            <p>
              <strong>Peak demand:</strong> {impact.peak_demand.definition}.
            </p>
          )}
          <p>
            <strong>Comparison basis:</strong> average per covered day, not window totals —
            the two windows are different lengths.
          </p>
          {impact.co2 && !impact.co2.available && (
            <p>
              <strong>CO₂:</strong> not reported — {impact.co2.reason}. The calculation is
              in place and will populate once a factor is agreed.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
